'use client'

import { useLatest } from '@/core/hooks/use-latest'
import { useLayoutEffect, useMemo, useRef } from 'react'
import type { Middleware } from 'swr'

import { useCachedSWR } from '@/core/lib/swr/use-cached-swr'
import { useAppReadScope } from '@/features/veluvit/hooks/use-app-read-scope'
import { appSwrKey } from '@/features/veluvit/lib/app-read'
import { bootstrapResponseSchema } from '@/features/veluvit/schemas/bootstrap.schema'
import {
  ApiError,
  getMutationGeneration,
} from '@/features/veluvit/services/api'
import { connectSessionReader } from '@/features/veluvit/services/session.service'
import { useSessionStore } from '@/features/veluvit/stores/session.store'

import type { FetcherResponse } from '@/core/lib/fetcher/core'
import type { Bootstrap } from '@/features/veluvit/types/api.types'

export default function BootstrapResource() {
  const appliedIdentities = useRef(new WeakMap<object, number>())
  const epoch = useAppReadScope()
  const activeScope = useLatest(epoch)
  const flight = useRef<{
    scope: string | null
    identity: number
    generation: number
    promise: Promise<Bootstrap>
  } | null>(null)
  const key = useMemo(() => (epoch ? appSwrKey(epoch) : null), [epoch])
  const middleware = useMemo<Middleware>(
    () => (useSWRNext) =>
      function useGuardedSWR(swrKey, fetcher, config) {
        const read =
          fetcher &&
          (async (...args: Parameters<typeof fetcher>) => {
            const identity = useSessionStore.getState().identity
            const scope = activeScope.current
            for (let attempt = 0; attempt < 2; attempt += 1) {
              const generation = getMutationGeneration()
              const response = await fetcher(...args)
              if (
                useSessionStore.getState().identity !== identity ||
                activeScope.current !== scope
              )
                throw new ApiError(
                  '계정이 바뀌어 조회를 종료했어요.',
                  0,
                  'SESSION_CHANGED'
                )
              if (getMutationGeneration() !== generation) continue
              const result = response as unknown as FetcherResponse<Bootstrap>
              if (result.ok) {
                useSessionStore.getState().applyBootstrap(result.data)
                appliedIdentities.current.set(
                  result,
                  useSessionStore.getState().identity
                )
              }
              return response
            }
            throw new ApiError(
              '변경 중인 내용을 저장한 뒤 다시 조회해 주세요.',
              0,
              'SESSION_BUSY'
            )
          })
        return useSWRNext(swrKey, read, config)
      },
    [activeScope]
  )
  const { mutate } = useCachedSWR<Bootstrap>(key, 'REALTIME', {
    responseSchema: bootstrapResponseSchema,
    revalidateOnMount: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
    use: [middleware],
  })

  useLayoutEffect(
    () =>
      connectSessionReader(() => {
        const identity = useSessionStore.getState().identity
        const generation = getMutationGeneration()
        if (
          flight.current?.scope === epoch &&
          flight.current.identity === identity &&
          flight.current.generation === generation
        )
          return flight.current.promise
        const promise = (async () => {
          const result = await mutate()
          const current = useSessionStore.getState()
          if (current.identity !== identity || activeScope.current !== epoch) {
            if (current.data) return current.data
            throw new ApiError(
              '계정이 바뀌어 조회를 종료했어요.',
              0,
              'SESSION_CHANGED'
            )
          }
          if (!result?.ok) throw new Error('앱 정보를 불러오지 못했어요.')
          if (
            useSessionStore.getState().identity !==
            appliedIdentities.current.get(result)
          )
            throw new ApiError(
              '계정이 바뀌어 조회를 종료했어요.',
              0,
              'SESSION_CHANGED'
            )
          return result.data
        })().finally(() => {
          if (flight.current?.promise === promise) flight.current = null
        })
        flight.current = { scope: epoch, identity, generation, promise }
        return promise
      }),
    [epoch, mutate, activeScope]
  )

  return null
}
